import React, { useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { VariantContextData } from "../VariantBuilderContext";
library.add(fas);
import VariantBuilderPanelTitle from "../VariantBuilderPanelTitle";
import useAiAssistant from "./useAiAssistant";
import {
  openSupportChat,
  buildSupportChatPrefill,
} from "../../../controllers/shared/chatFeedback";
import aiFocusBreadcrumb, { shortBreadcrumb } from "./aiFocusBreadcrumb";
import RemixSparkleIcon from "./RemixSparkleIcon";
import AttachmentUploader from "./AttachmentUploader";
import BillableActionsMeter from "./BillableActionsMeter";
import {
  REMIX_AI_WARNING_CLASS,
  REMIX_AI_WARNING_TEXT,
} from "../../../controllers/shared/remixAiChatUi";
import {
  canUseAiRemixBuilderChat,
  remixAiUpgradePlan,
} from "../../../utils/canUseAiRemixText";
import {
  attachImportedAssetsFromEvent,
  attachmentFromClipboard,
  htmlOrMarkdownFilesFromDataTransfer,
  MAX_IMPORTED_ATTACHMENTS,
} from "../../../utils/aiPasteAttachment";
import { markdownContentBlocks } from "../../../utils/aiChatMarkdown";
import {
  dataTransferHasFiles,
  dataTransferHasImages,
  UPLOAD_WAIT_MESSAGE,
  uploadFailureMessage,
} from "../../../utils/aiChatImageDrop";

const RemixAiWarning = () => (
  <p className={REMIX_AI_WARNING_CLASS}>{REMIX_AI_WARNING_TEXT}</p>
);

const SUGGESTIONS = [
  "Improve my copywriting",
  "Update the theme styling",
  "Build a funnel step",
  "Create a quiz",
  "Add conditional logic",
];

const IMPORTABLE_ATTACHMENT_TYPES = ["image", "html", "markdown"];
const CONDITIONAL_ROW_ATTACHMENT_TYPES = ["image", "html", "markdown", "csv"];
const MAX_CHAT_ATTACHMENTS = 5;

// import-target suggestions shown when an asset is attached, narrowed by the
// chat's focused object (deeper focus = fewer, more specific targets)
export const assetImportOptions = (focus, variant) => {
  const type = focus && focus.object_type;
  if (type === "elements" && variant) {
    const element =
      (variant.elements || {})[focus.object_id] ||
      (variant.elements || {})[String(focus.object_id)];
    if (element && element.element_type === "conditional_row") {
      return ["Populate conditional row"];
    }
  }
  if (["sections", "section_columns", "elements", "fields"].includes(type)) {
    return ["Add row within content"];
  }
  if (type === "steps") {
    return ["Build a section", "Add row within content"];
  }
  return ["Build a funnel step", "Build a section", "Add row within content"];
};

const focusedConditionalRow = (focus, variant) => {
  if (!focus || focus.object_type !== "elements" || !variant) return false;
  const element =
    (variant.elements || {})[focus.object_id] ||
    (variant.elements || {})[String(focus.object_id)];
  return !!(element && element.element_type === "conditional_row");
};

const showAssetImportOptions = (attachments, focus, variant) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return false;
  if (focusedConditionalRow(focus, variant)) {
    return attachments.some((attachment) =>
      CONDITIONAL_ROW_ATTACHMENT_TYPES.includes(attachment.type)
    );
  }
  return attachments.some((attachment) =>
    IMPORTABLE_ATTACHMENT_TYPES.includes(attachment.type)
  );
};

// playbook chat pill styling (playbook_chat_controller.js)
const PILL_STYLE = {
  backgroundColor: "#E6ECFF",
  border: "1px solid #D0D8FF",
  color: "#003FFF",
};
const PILL_CLASS =
  "inline-flex items-center justify-start gap-2 px-4 py-2 rounded-lg text-left text-xl transition-colors cursor-pointer";

// blue ConvertFlow avatar shown next to assistant messages (playbook chat)
const AssistantAvatar = ({ logoWhite }) => (
  <div
    className="w-10 h-10 rounded-full bg-[#003FFF] flex items-center justify-center flex-shrink-0 p-2"
    style={{ marginTop: "10px" }}
  >
    {logoWhite ? (
      <img src={logoWhite} alt="" className="w-full h-full object-contain" />
    ) : (
      <RemixSparkleIcon width={18} height={19} color="#FFFFFF" />
    )}
  </div>
);

const renderBoldText = (text) =>
  String(text)
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) => {
      const bold = part.match(/^\*\*([^*]+)\*\*$/);
      return bold ? <strong key={index}>{bold[1]}</strong> : part;
    });

const MarkdownTable = ({ headers, alignments, rows }) => (
  <div className="playbook-chat-md-table-wrap overflow-x-auto my-2 max-w-full">
    <table className="playbook-chat-md-table w-full border-collapse text-left">
      <thead>
        <tr>
          {headers.map((cell, index) => (
            <th
              key={`h-${index}`}
              className={`border border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 px-2 py-1.5 font-semibold ${
                alignments[index] === "center"
                  ? "text-center"
                  : alignments[index] === "right"
                  ? "text-right"
                  : "text-left"
              }`}
              data-align={alignments[index] || "left"}
            >
              {renderBoldText(cell)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`r-${rowIndex}`}>
            {row.map((cell, index) => (
              <td
                key={`c-${rowIndex}-${index}`}
                className={`border border-gray-300 dark:border-slate-600 px-2 py-1.5 ${
                  alignments[index] === "center"
                    ? "text-center"
                    : alignments[index] === "right"
                    ? "text-right"
                    : "text-left"
                }`}
                data-align={alignments[index] || "left"}
              >
                {renderBoldText(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const renderRichText = (text) =>
  markdownContentBlocks(text).map((block, blockIndex) => {
    if (block.type === "table") {
      return (
        <MarkdownTable
          key={`table-${blockIndex}`}
          headers={block.headers}
          alignments={block.alignments}
          rows={block.rows}
        />
      );
    }
    return (
      <span key={`text-${blockIndex}`} className="whitespace-pre-wrap">
        {renderBoldText(block.text)}
      </span>
    );
  });

// thumbs up/down shown under each assistant reply; records the conversation for
// the product team once clicked
const MessageFeedback = ({ feedback, onRate, messages }) => {
  const submitted = Boolean(feedback);
  const buttonClass = (active, activeColor) =>
    `p-0 bg-transparent border-0 leading-none transition-colors ${
      submitted ? "cursor-default" : "cursor-pointer"
    } ${
      active
        ? activeColor
        : `text-slate-300 dark:text-slate-600 ${
            submitted ? "" : "hover:text-slate-500 dark:hover:text-slate-400"
          }`
    }`;

  return (
    <div className="flex items-center flex-nowrap gap-3 mt-1 ml-1 text-xl">
      <button
        type="button"
        className={buttonClass(feedback === "up", "text-black dark:text-white")}
        title="Good response"
        disabled={submitted}
        onClick={() => onRate("up")}
      >
        <FontAwesomeIcon icon="fas fa-thumbs-up" />
      </button>
      <button
        type="button"
        className={buttonClass(
          feedback === "down",
          "text-black dark:text-white"
        )}
        title="Bad response"
        disabled={submitted}
        onClick={() => onRate("down")}
      >
        <FontAwesomeIcon icon="fas fa-thumbs-down" />
      </button>
      {submitted && (
        <>
          <span className="text-slate-400 dark:text-slate-500 whitespace-nowrap">
            Thanks!
          </span>
          {typeof window.Intercom === "function" && (
            <>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <button
                type="button"
                className="p-0 bg-transparent border-0 text-blue-600 hover:underline cursor-pointer whitespace-nowrap"
                onClick={() =>
                  openSupportChat(buildSupportChatPrefill(messages))
                }
              >
                Get support →
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

const AIChatPanel = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const {
    messages,
    loading,
    progressLabel,
    sendMessage,
    sendFeedback,
    focus,
    setFocus,
  } = useAiAssistant();
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [pendingUploads, setPendingUploads] = useState(0);
  const [dropError, setDropError] = useState("");
  const pendingUploadsRef = useRef(0);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // grow the composer with its content, up to ~6 lines, then scroll
  useEffect(() => {
    const textarea = textareaRef.current;
    // skip while the panel is display:none (scrollHeight reads 0 and would
    // collapse the input); re-runs when the tab becomes visible via view dep
    if (!textarea || textarea.offsetParent === null) return;

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(
      Math.max(textarea.scrollHeight, 28),
      150
    )}px`;
  }, [draft, contextData.view]);

  // focus the input whenever the Remix AI tab is opened
  useEffect(() => {
    if (contextData.view === "ai" && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [contextData.view]);

  const website = contextData.objects.website;
  const account = contextData.objects.account;
  const currentUser = contextData.objects.current_user;
  const userAvatar = currentUser && currentUser.avatar;
  const subscription = contextData.objects.subscription;
  const aiUnlocked = canUseAiRemixBuilderChat({
    currentUser: currentUser,
    website: website,
    subscription: subscription,
    account: account,
  });

  const focusBreadcrumb = aiFocusBreadcrumb(contextData, builder, focus);

  // select + scroll to a breadcrumb's builder object (no-op if it was deleted)
  const goToObject = (target) => {
    if (!target || !target.object_type || !target.object_id) return;

    const object = (contextData.objects.variant[target.object_type] || {})[
      target.object_id
    ];
    if (!object || object.toBeDeleted === true) return;

    dispatchCustomEvent("selectObject", {
      object_type: target.object_type,
      object_id: target.object_id,
      panel_closed: false,
    });
    builder.scrollToObjectWithRetry(target.object_type, target.object_id);
  };

  // Scroll only the messages list — scrollIntoView also scrolls ancestor
  // .options-scrollable and leaves empty space under the composer.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;

    const optionsScrollable = document.querySelector(
      "#options-panel .options-scrollable"
    );
    if (optionsScrollable) {
      optionsScrollable.scrollTop = 0;
    }
  }, [messages.length, loading]);

  // prompts dispatched from elsewhere in the builder (e.g. the create
  // panels' Remix AI prompt bar); the panel stays mounted so this always runs
  useEffect(() => {
    const handlePromptEvent = (event) => {
      const detail = event.detail || {};
      if (!detail.prompt) return;

      if (!aiUnlocked) {
        triggerRemixGate();
        return;
      }

      if (Array.isArray(detail.attachments) && detail.attachments.length > 0) {
        sendMessage(detail.prompt, detail.attachments);
      } else {
        submit(detail.prompt);
      }
    };
    document.addEventListener("remixAiPrompt", handlePromptEvent);

    return () =>
      document.removeEventListener("remixAiPrompt", handlePromptEvent);
  });

  const triggerRemixGate = (event) => {
    if (typeof subscriptions === "undefined" || !subscriptions.upgrade_modal) {
      return;
    }

    const limit = Number(account?.billable_actions_limit);
    const usage = Number(account?.billable_actions_usage);
    const freePlan = String(subscription?.plan_type || "").trim() === "free";
    const exhausted =
      Number.isFinite(limit) &&
      limit > 0 &&
      Number.isFinite(usage) &&
      usage >= limit;

    subscriptions.upgrade_modal(
      event || window.event,
      false,
      exhausted
        ? freePlan
          ? `continue with ConvertFlow AI after using all ${limit} included AI actions.`
          : `continue with ConvertFlow AI after using all ${limit} actions this month.`
        : "build funnels with ConvertFlow AI",
      remixAiUpgradePlan({ subscription: subscription, website: website }),
      exhausted
        ? "Triggered feature CTA – Remix AI actions limit"
        : "Triggered feature CTA – Remix AI Builder Chat"
    );
  };

  const composerReady =
    (!!draft.trim() || attachments.length > 0) &&
    !loading &&
    pendingUploads === 0;

  const submit = (text) => {
    const prompt = text !== undefined ? text : draft;
    const trimmed = (prompt || "").trim();
    if ((!trimmed && attachments.length === 0) || loading || pendingUploads > 0)
      return;

    if (!aiUnlocked) {
      triggerRemixGate();
      return;
    }
    if (uploadsInFlight.current > 0) {
      setDropError(UPLOAD_WAIT_MESSAGE);
      return;
    }

    const message =
      trimmed ||
      (attachments.some((attachment) => attachment.type === "image")
        ? "Use the attached images in the relevant settings for the focused question or answers."
        : "Use the attached files.");

    sendMessage(message, attachments);
    setDraft("");
    setAttachments([]);
    setDropError("");
  };

  const trackUpload = (delta) => {
    const next = Math.max(0, uploadsInFlight.current + delta);
    uploadsInFlight.current = next;
    pendingUploadsRef.current = next;
    setPendingUploads(next);
    if (next === 0) {
      setDropError((prev) => (prev === UPLOAD_WAIT_MESSAGE ? "" : prev));
    }
  };

  const addAttachment = (attachment) => {
    setAttachments((existing) => {
      if (existing.length >= MAX_CHAT_ATTACHMENTS) {
        Promise.resolve().then(() =>
          setDropError(
            `You can attach up to ${MAX_CHAT_ATTACHMENTS} attachments.`
          )
        );
        return existing;
      }
      return [...existing, attachment];
    });
  };

  const uploadsInFlight = useRef(0);

  const importAssetsFromEvent = (event) =>
    attachImportedAssetsFromEvent(event, {
      addAttachment,
      website,
      environment: contextData.environment,
      attachmentCount: attachments.length,
      maxAttachments: MAX_IMPORTED_ATTACHMENTS,
      pendingUploadsRef,
      onPendingUploadsChange: (count) => {
        const next = Number.isFinite(count) ? count : pendingUploadsRef.current;
        uploadsInFlight.current = next;
        pendingUploadsRef.current = next;
        setPendingUploads(next);
      },
      onLimitMessage: setDropError,
    });

  const handlePaste = (event) => {
    const transfer = event.clipboardData;
    const files = Array.from((transfer && transfer.files) || []);
    const hasImport =
      files.length > 0 || Boolean(attachmentFromClipboard(transfer));
    if (!hasImport) return;
    event.preventDefault();
    if (!aiUnlocked) {
      triggerRemixGate(event);
      return;
    }
    importAssetsFromEvent(event);
  };

  const handleComposerDragOver = (event) => {
    if (!dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect =
      dataTransferHasImages(event.dataTransfer) ||
      htmlOrMarkdownFilesFromDataTransfer(event.dataTransfer).length > 0
        ? "copy"
        : "none";
  };

  const handleComposerDrop = (event) => {
    if (!dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    if (!aiUnlocked) {
      triggerRemixGate(event);
      return;
    }
    importAssetsFromEvent(event);
  };

  const lastMessage = messages[messages.length - 1];
  const clarifyingOptions =
    !loading &&
    lastMessage &&
    lastMessage.role === "assistant" &&
    lastMessage.clarifyingQuestion
      ? lastMessage.clarifyingQuestion.options || []
      : [];

  return (
    <div
      className="flex flex-col flex-1 w-full h-full min-h-0 select-text"
      data-testid="ai-chat-panel"
      onDragOver={handleComposerDragOver}
      onDragEnter={handleComposerDragOver}
      onDrop={handleComposerDrop}
    >
      {/* w-full block wrapper: as a flex item, the title's own margin:auto
          (hard-center) would otherwise shrink + center it */}
      <div className="w-full shrink-0">
        <VariantBuilderPanelTitle title="ConvertFlow AI" showBeta />
      </div>

      {
        <div className="flex flex-col flex-1 min-h-0">
          <div
            ref={messagesContainerRef}
            className="flex-1 flex flex-col overflow-y-auto min-h-0 -mx-[9px] pt-4"
          >
            {messages.map((message, index) =>
              message.role === "user" ? (
                <div key={index} className="flex justify-end gap-2 mb-[32px]">
                  <div className="inline-flex flex-col items-end min-w-0 max-w-[85%]">
                    <div
                      className="inline-block max-w-full px-4 py-3 text-xl leading-relaxed text-gray-800 break-words overflow-hidden"
                      style={{
                        backgroundColor: "#F9F9F9",
                        border: "1px solid #E5E5E5",
                        borderRadius: "12px 5px 12px 12px",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {message.focus && (
                        <button
                          className="flex items-center gap-1 mb-1 p-0 text-lg leading-none text-left whitespace-nowrap opacity-80 bg-transparent border-0 text-inherit cursor-pointer hover:opacity-100 hover:underline"
                          title={`Go to ${message.focus.label}`}
                          onClick={() => goToObject(message.focus)}
                        >
                          <FontAwesomeIcon icon="fas fa-arrow-pointer" />
                          {shortBreadcrumb(message.focus.label)}
                        </button>
                      )}
                      <span className="whitespace-pre-wrap">
                        {message.content}
                      </span>
                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <div className="mt-2 text-lg opacity-80">
                            <FontAwesomeIcon icon="fas fa-paperclip" />{" "}
                            {message.attachments
                              .map((attachment) => attachment.name)
                              .join(", ")}
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden bg-gray-200">
                    {userAvatar ? (
                      <img
                        src={userAvatar}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon="fas fa-user"
                        className="text-gray-500"
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div key={index} className="flex justify-start gap-2 mb-[32px]">
                  <AssistantAvatar logoWhite={props.logo_white} />
                  {/* avatar (2.5rem) + gap (0.5rem) + the messages list's 9px
                      right bleed, so the bubble stops at the panel clip edge */}
                  <div className="flex flex-col min-w-0 max-w-[calc(100%-3rem-9px)]">
                    <div
                      className={`px-4 py-3 text-xl leading-relaxed break-words bg-slate-100 dark:bg-slate-800 ${
                        message.error
                          ? "text-red-600 dark:text-red-400"
                          : "text-gray-800 dark:text-white"
                      }`}
                      style={{ borderRadius: "5px 12px 12px 12px" }}
                    >
                      <div className="min-w-0">
                        {renderRichText(message.content)}
                      </div>
                      {message.clarifyingQuestion && (
                        <div className="mt-2">
                          {message.clarifyingQuestion.question}
                        </div>
                      )}
                    </div>
                    {index === 0 && <RemixAiWarning />}
                    {index > 0 && !message.error && (
                      <MessageFeedback
                        feedback={message.feedback}
                        onRate={(rating) => sendFeedback(index, rating)}
                        messages={messages.slice(0, index + 1)}
                      />
                    )}
                  </div>
                </div>
              )
            )}

            {clarifyingOptions.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-[32px] ml-12">
                {clarifyingOptions.map((option) => (
                  <button
                    key={option}
                    className={PILL_CLASS}
                    style={PILL_STYLE}
                    onClick={() => submit(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start items-center gap-2 mb-[32px]">
                <div className="w-7 h-7 rounded-full bg-[#003FFF] flex items-center justify-center flex-shrink-0 p-1.5">
                  {props.logo_white ? (
                    <img
                      src={props.logo_white}
                      alt=""
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <RemixSparkleIcon width={12} height={13} color="#FFFFFF" />
                  )}
                </div>
                <div
                  className="px-3 h-7 text-gray-800 dark:text-white flex items-center gap-2 bg-slate-100 dark:bg-slate-800"
                  style={{ borderRadius: "4px 10px 10px 10px" }}
                >
                  {progressLabel && (
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      {progressLabel}
                    </span>
                  )}
                  <span
                    className="playbook-chat-typing-dots playbook-chat-typing-dots--sm"
                    role="status"
                  >
                    <span className="sr-only">Assistant is typing</span>
                    <span aria-hidden="true">.</span>
                    <span aria-hidden="true">.</span>
                    <span aria-hidden="true">.</span>
                  </span>
                </div>
              </div>
            )}

            {!messages.some((message) => message.role === "user") && (
              <div className="mt-auto pb-2">
                <p className="text-xl font-semibold text-black dark:text-white mb-2">
                  Suggestions...
                </p>
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    className="flex items-center gap-3 w-full text-left text-xl mb-2 px-4 py-3 rounded-xl border-0 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-800"
                    onClick={() => submit(suggestion)}
                  >
                    <RemixSparkleIcon width={13} height={14} color="#003FFF" />
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="shrink-0 -mx-[17px] overflow-hidden bg-white dark:bg-slate-950 rounded-t-2xl rounded-b-xl border-0 border-t border-solid border-slate-100 dark:border-slate-800 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
            <div className="px-4 pt-4 pb-2">
              {showAssetImportOptions(
                attachments,
                focus,
                contextData.objects.variant
              ) && (
                <div className="flex flex-wrap gap-2 pb-3">
                  {assetImportOptions(focus, contextData.objects.variant).map(
                    (option) => (
                      <button
                        key={option}
                        className={PILL_CLASS}
                        style={PILL_STYLE}
                        onClick={() => submit(option)}
                      >
                        {option}
                      </button>
                    )
                  )}
                </div>
              )}
              {dropError && (
                <p className="mb-2 text-lg text-red-600">{dropError}</p>
              )}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 pb-2">
                  {attachments.map((attachment, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-lg text-lg border border-solid border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-black dark:text-white"
                    >
                      <FontAwesomeIcon
                        icon="fas fa-paperclip"
                        className="mr-2"
                      />
                      {attachment.name}
                      <button
                        className="ml-2 bg-transparent border-0 cursor-pointer text-slate-500 p-0"
                        onClick={() => {
                          setDropError("");
                          setAttachments((existing) =>
                            existing.filter((_, i) => i !== index)
                          );
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-start gap-2">
                <span className="shrink-0">
                  <RemixSparkleIcon width={12} height={13} />
                </span>
                <textarea
                  ref={textareaRef}
                  className="flex-1 min-w-0 min-h-[28px] max-h-[150px] text-xl p-0 bg-transparent text-gray-900 dark:text-white resize-none overflow-y-auto overflow-x-hidden border-none outline-none focus:border-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 shadow-none focus:shadow-none placeholder-gray-500 leading-relaxed break-words"
                  style={{ border: 0, outline: "none", boxShadow: "none" }}
                  rows={1}
                  placeholder="Ask to edit or customize..."
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onPaste={handlePaste}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      submit();
                    }
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="relative flex items-center gap-2 min-w-0">
                  <AttachmentUploader
                    onAttach={addAttachment}
                    onUploadBusy={trackUpload}
                    onUploadError={(reason) =>
                      setDropError(uploadFailureMessage(new Error(reason)))
                    }
                  />
                  {focusBreadcrumb && (
                    <div
                      className="h-[30px] min-w-0 flex items-center pb-[2px] gap-2 px-3 rounded-full border border-solid border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-black dark:text-white text-xl hover:border-blue-600"
                      data-testid="ai-focus-chip"
                    >
                      <button
                        className="min-w-0 flex items-center gap-2 p-0 bg-transparent border-0 text-inherit text-xl cursor-pointer"
                        title={`Go to ${focusBreadcrumb}`}
                        onClick={() => goToObject(focus)}
                      >
                        <FontAwesomeIcon icon="fas fa-arrow-pointer" />
                        <span className="truncate whitespace-nowrap">
                          {shortBreadcrumb(focusBreadcrumb)}
                        </span>
                      </button>
                      <button
                        className="shrink-0 p-0 bg-transparent border-0 cursor-pointer text-slate-500 hover:text-black dark:hover:text-white"
                        title="Remove focus"
                        onClick={() => setFocus(null)}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className={`w-[30px] h-[30px] shrink-0 ml-2 flex items-center justify-center rounded-full border-0 bg-[#003FFF] text-white transition-colors p-0 ${
                    composerReady
                      ? "cursor-pointer hover:bg-[#0033e0]"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  style={{ minWidth: "30px", minHeight: "30px" }}
                  title="Send"
                  disabled={!composerReady}
                  onClick={() => submit()}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 3V15M9 3L4 8M9 3L14 8"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <BillableActionsMeter
              usage={account && account.billable_actions_usage}
              limit={account && account.billable_actions_limit}
              accountId={account && account.id}
              websiteId={website && website.id}
            />
          </div>
        </div>
      }
    </div>
  );
};

export default AIChatPanel;
